export interface VendingInventorySelectedPack {
  pack_id?: number | string | null;
  remaining_qty?: number | string | null;
}

const toOptionalNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toOptionalPackId = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  if (Number.isFinite(parsed)) {
    return String(parsed);
  }

  const trimmed = String(value).trim();
  return trimmed || null;
};

export const normalizeInventorySelectedPack = (value: unknown): VendingInventorySelectedPack => {
  const pack = (value || {}) as Record<string, unknown>;
  return {
    pack_id: toOptionalPackId(pack.pack_id ?? pack.selected_pack_id ?? pack.pack),
    remaining_qty: toOptionalNumber(pack.remaining_qty ?? pack.quantity),
  };
};

export const createEmptyInventorySelectedPack = (): VendingInventorySelectedPack => ({
  pack_id: null,
  remaining_qty: null,
});

export const getInventorySelectedPackSlots = (line: any): VendingInventorySelectedPack[] => {
  const selectedPacks = Array.isArray(line?.selected_packs) ? line.selected_packs : [];
  const requiredPackCount = toOptionalNumber(line?.required_pack_count) ?? 0;
  const slotCount = Math.max(requiredPackCount, selectedPacks.length, 1);

  return Array.from({ length: slotCount }, (_, index) => normalizeInventorySelectedPack(selectedPacks[index]));
};

export const appendInventorySelectedPackSlot = (line: any): VendingInventorySelectedPack[] => [
  ...getInventorySelectedPackSlots(line),
  createEmptyInventorySelectedPack(),
];

export const removeInventorySelectedPackSlot = (
  line: any,
  packIndex: number,
): VendingInventorySelectedPack[] => {
  const slots = getInventorySelectedPackSlots(line);
  if (slots.length <= 1) {
    return slots;
  }

  return slots.filter((_, index) => index !== packIndex);
};

export const getInventorySelectedPackAggregateQuantity = (line: any): number => (
  getInventorySelectedPackSlots(line).reduce((sum, pack) => sum + (toOptionalNumber(pack.remaining_qty) ?? 0), 0)
);

export const getInventorySelectedPackAggregateValue = (
  line: any,
  gamesData: Map<string, number> | null,
): number => {
  const gameId = line?.game != null ? String(line.game).trim() : '';
  const unitPrice = gameId && gamesData ? Number(gamesData.get(gameId) || 0) : 0;

  return getInventorySelectedPackAggregateQuantity(line) * unitPrice;
};

export const getInventorySelectedPackDisplayValue = (
  line: any,
  pack: VendingInventorySelectedPack,
  gamesData: Map<string, number> | null,
): number => {
  const gameId = line?.game != null ? String(line.game).trim() : '';
  const unitPrice = gameId && gamesData ? Number(gamesData.get(gameId) || 0) : 0;
  const quantity = toOptionalNumber(pack.remaining_qty) ?? 0;
  return quantity * unitPrice;
};

export const getInventoryPrimaryPackId = (line: any): number | string | null => {
  const selectedPacks = getInventorySelectedPackSlots(line);
  const primaryPack = selectedPacks.find((pack) => pack.pack_id !== null && pack.pack_id !== undefined && String(pack.pack_id).trim() !== '');
  return primaryPack?.pack_id ?? null;
};
